import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowLeft, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AuthShell } from "@/pages/Login";
import { forgotPassword } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-display text-3xl font-black tracking-tight">Forgot password?</h1>
      <p className="text-sm text-slate-500 mt-1 leading-relaxed">
        Enter your account email and we'll send you a secure link to choose a new password.
      </p>

      {sent ? (
        <div
          className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 dark:bg-mint/10 dark:border-mint/30 p-5"
          data-testid="forgot-sent-card"
        >
          <div className="flex items-start gap-3">
            <CheckCircle size={22} weight="fill" className="text-emerald-500 dark:text-mint shrink-0 mt-0.5" />
            <div className="text-sm text-slate-800 leading-relaxed">
              If an account exists for <strong className="text-slate-950">{email}</strong>, we just
              sent a reset link. It expires in 1 hour. Check your inbox (and spam folder).
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
              Email
            </Label>
            <div className="relative mt-1.5">
              <EnvelopeSimple
                size={14}
                weight="bold"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-11 rounded-md border-slate-300 pl-9 focus-visible:ring-0 focus-visible:border-slate-950"
                data-testid="forgot-email-input"
                autoFocus
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading || !email.trim()}
            className="w-full h-11 bg-slate-950 hover:bg-slate-800 text-white rounded-md font-medium"
            data-testid="forgot-submit-btn"
          >
            {loading ? "Sending…" : (
              <span className="flex items-center gap-2 justify-center">
                Send reset link <ArrowRight size={14} weight="bold" />
              </span>
            )}
          </Button>
        </form>
      )}

      <div className="mt-6 text-center">
        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-950"
          data-testid="forgot-back-to-login"
        >
          <ArrowLeft size={12} weight="bold" />
          Back to log in
        </Link>
      </div>
    </AuthShell>
  );
}
